import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Inbox, Tags, FileText, Settings, Type, MessageSquare, Building2, Users, LineChart, UserCog, BadgePercent, Newspaper, Layers, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import OrdersView from "@/components/admin/OrdersView";
import InquiriesView from "@/components/admin/InquiriesView";
import PricingView from "@/components/admin/PricingView";
import DiscountsView from "@/components/admin/DiscountsView";
import BlogView from "@/components/admin/BlogView";
import InvoicesView from "@/components/admin/InvoicesView";
import ContentView from "@/components/admin/ContentView";
import NavTabsView from "@/components/admin/NavTabsView";
import CompanyView from "@/components/admin/CompanyView";
import SettingsView from "@/components/admin/SettingsView";
import CustomersView from "@/components/admin/CustomersView";
import RevenueStatsView from "@/components/admin/RevenueStatsView";
import StaffView from "@/components/admin/StaffView";
import ProtectionView from "@/components/admin/ProtectionView";

const tabs = [
  { id: "orders", label: "Bestellungen", Icon: Inbox },
  { id: "customers", label: "Kunden", Icon: Users },
  { id: "inquiries", label: "Anfragen", Icon: MessageSquare },
  { id: "pricing", label: "Preise", Icon: Tags },
  { id: "discounts", label: "Rabattcodes", Icon: BadgePercent },
  { id: "revenue", label: "Umsatzstatistik", Icon: LineChart },
  { id: "invoices", label: "Rechnungen", Icon: FileText },
  { id: "blog", label: "Ratgeber", Icon: Newspaper },
  { id: "content", label: "Inhalte", Icon: Type },
  { id: "nav", label: "Tabs", Icon: Layers },
  { id: "protection", label: "Schutzpakete", Icon: ShieldCheck },
  { id: "company", label: "Firmendaten", Icon: Building2 },
  { id: "settings", label: "Einstellungen", Icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const visibleTabs = isAdmin
    ? [...tabs, { id: "staff", label: "Mitarbeiter", Icon: UserCog }]
    : tabs.filter((t) => (user?.permissions || []).includes(t.id));
  const [tab, setTab] = useState(visibleTabs[0]?.id || "orders");

  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some((t) => t.id === tab)) {
      setTab(visibleTabs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  // ESC verlässt den Admin-Bereich und öffnet die öffentliche Startseite.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") navigate("/");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#050505] text-white grain flex flex-col">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <a href="/" className="font-display text-2xl flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-white" />
            G-Repair <span className="text-[#666] text-sm font-sans ml-2">Admin</span>
          </a>
          <div className="flex items-center gap-5">
            <span className="hidden md:block text-xs text-[#666] font-mono">ESC zum Verlassen</span>
            <span className="hidden sm:block text-sm text-[#888]">{user?.email}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 text-sm text-[#ccc] hover:text-white transition-colors">
              <LogOut size={16} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] w-full mx-auto px-4 sm:px-6 lg:px-10 flex-1 flex flex-col lg:flex-row gap-5 lg:gap-8 py-5 lg:py-8">
        {/* Sidebar / mobile tab bar */}
        <nav className="flex lg:flex-col gap-2 shrink-0 overflow-x-auto no-scrollbar -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0 lg:w-56 lg:overflow-visible">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 lg:gap-3 px-3.5 lg:px-4 py-2.5 lg:py-3 rounded-xl text-sm whitespace-nowrap shrink-0 lg:w-full lg:shrink transition-colors duration-300 ${
                tab === t.id ? "bg-white text-[#050505]" : "bg-white/[0.03] lg:bg-transparent border border-white/10 lg:border-transparent text-[#aaa] hover:text-white hover:bg-white/5"
              }`}
            >
              <t.Icon size={16} className="shrink-0 lg:w-[18px] lg:h-[18px]" /> {t.label}
            </button>
          ))}
        </nav>

        <main className="flex-1 min-w-0">
          <ErrorBoundary>
            {visibleTabs.length === 0 && (
              <div data-testid="no-permissions" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-10 text-center text-[#888]">
                Dir wurden noch keine Berechtigungen zugewiesen. Bitte wende dich an den Administrator.
              </div>
            )}
            {tab === "orders" && <OrdersView />}
            {tab === "customers" && <CustomersView />}
            {tab === "inquiries" && <InquiriesView />}
            {tab === "pricing" && <PricingView />}
            {tab === "discounts" && <DiscountsView />}
            {tab === "revenue" && <RevenueStatsView />}
            {tab === "invoices" && <InvoicesView />}
            {tab === "blog" && <BlogView />}
            {tab === "content" && <ContentView />}
            {tab === "nav" && <NavTabsView />}
            {tab === "protection" && <ProtectionView />}
            {tab === "company" && <CompanyView />}
            {tab === "settings" && <SettingsView />}
            {tab === "staff" && isAdmin && <StaffView />}
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
